// frontend/src/components/documents/DocumentUploader.js
// Phase 3: Document Upload Component with Drag & Drop

import React, { useState, useCallback } from 'react';
import {
  Box, Paper, Typography, LinearProgress, Chip, 
  Button, IconButton, Alert, List, ListItem, ListItemText, 
  ListItemSecondaryAction, ListItemIcon, CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloudIcon from '@mui/icons-material/Cloud';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableViewIcon from '@mui/icons-material/TableView';
import CodeIcon from '@mui/icons-material/Code';
import axios from 'axios';

const DocumentUploader = ({ brandId, onDocumentProcessed, expertId = null, agentId = null, maxFiles = 5 }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [processingStatus, setProcessingStatus] = useState({});
  const [errors, setErrors] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const getFileIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return <PictureAsPdfIcon />;
    if (mimeType.includes('wordprocessingml') || mimeType.includes('msword')) return <ArticleIcon />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return <TableViewIcon />;
    if (mimeType.includes('json')) return <CodeIcon />;
    return <InsertDriveFileIcon />;
  };

  const validateFile = (file) => {
    const supportedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'text/plain',
      'text/markdown',
      'application/json'
    ];

    if (!supportedTypes.includes(file.type)) {
      return `Unsupported file type: ${file.type}`;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB
      return `File too large: ${formatFileSize(file.size)}. Maximum size is 50MB.`;
    }

    return null;
  };

  const uploadFile = async (file) => {
    const fileId = `${file.name}_${Date.now()}`;
    
    try {
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
      
      // Validate that an owner (expert or agent) is provided
      if (!expertId && !agentId) {
        throw new Error('Expert or Agent must be selected before uploading documents');
      }

      const formData = new FormData();
      formData.append('document', file);
      formData.append('brandId', brandId);
      if (agentId) {
        formData.append('agentId', agentId);
      } else {
        formData.append('expertId', expertId);
      }

      console.log(`[DocumentUploader] Uploading ${file.name} to brand ${brandId} for ${agentId ? 'agent' : 'expert'} ${agentId || expertId}`);
      
      const response = await axios.post('/api/chat/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(prev => ({ ...prev, [fileId]: percentCompleted }));
        },
      });
      
      console.log(`[DocumentUploader] Upload response:`, response.data);
      
      if (response.data.success) {
        setUploadedFiles(prev => [...prev, { 
          ...response.data.document,
          fileId,
          uploadedAt: new Date(),
          originalFile: file,
          method: response.data.method // 'gemini_file_search' or 'legacy_chunking'
        }]);
        
        // Start monitoring processing status
        monitorProcessingStatus(response.data.document.document_id, fileId);
        
        // Remove from upload progress after a delay
        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error(`[DocumentUploader] Upload failed for ${file.name}:`, error);
      
      setErrors(prev => [...prev, {
        filename: file.name,
        error: error.response?.data?.error || error.message || 'Upload failed'
      }]);
      
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
    }
  };

  const monitorProcessingStatus = async (documentId, fileId) => {
    const maxPolls = 30; // 5 minutes
    let pollCount = 0;
    
    const poll = async () => {
      try {
        console.log(`[DocumentUploader] Checking processing status for ${documentId}`);
        
        // Use owner-specific status endpoint
        const ownerId = agentId || expertId;
        const ownerTypeParam = agentId ? '?ownerType=agent' : '';
        const statusEndpoint = ownerId
          ? `/api/chat/documents/${brandId}/${ownerId}/status/${documentId}${ownerTypeParam}`
          : `/api/chat/documents/${brandId}/${documentId}/status`;
        
        const response = await axios.get(statusEndpoint);
        const data = response.data;
        
        setProcessingStatus(prev => ({
          ...prev,
          [fileId]: data.status || data.processing_status
        }));
        
        if (data.status === 'ready' || data.processed) {
          console.log(`[DocumentUploader] Document ${documentId} processing completed`);
          onDocumentProcessed?.(documentId, data);
          return;
        }
        
        if (data.status === 'failed') {
          setErrors(prev => [...prev, {
            filename: fileId,
            error: 'Document processing failed'
          }]);
          return;
        }
        
        if (pollCount < maxPolls && (data.status === 'processing' || !data.processed)) {
          pollCount++;
          setTimeout(poll, 10000); // Poll every 10 seconds
        }
      } catch (error) {
        console.error('Status polling failed:', error);
        // Don't add error for polling failures as they're not user-facing
      }
    };
    
    setTimeout(poll, 2000); // Start polling after 2 seconds
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    
    if (droppedFiles.length > maxFiles) {
      setErrors(prev => [...prev, {
        filename: 'Multiple files',
        error: `Too many files. Maximum ${maxFiles} files allowed.`
      }]);
      return;
    }
    
    // Validate all files first
    const validationErrors = [];
    const validFiles = [];
    
    droppedFiles.forEach(file => {
      const error = validateFile(file);
      if (error) {
        validationErrors.push({ filename: file.name, error });
      } else {
        validFiles.push(file);
      }
    });
    
    if (validationErrors.length > 0) {
      setErrors(prev => [...prev, ...validationErrors]);
    }
    
    // Upload valid files
    for (const file of validFiles) {
      await uploadFile(file);
    }
  }, [brandId, expertId, agentId, maxFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleFileInput = useCallback(async (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Reset file input
    e.target.value = '';

    if (selectedFiles.length > maxFiles) {
      setErrors(prev => [...prev, {
        filename: 'Multiple files',
        error: `Too many files. Maximum ${maxFiles} files allowed.`
      }]);
      return;
    }

    // Process files same as drag & drop
    for (const file of selectedFiles) {
      const error = validateFile(file);
      if (error) {
        setErrors(prev => [...prev, { filename: file.name, error }]);
      } else {
        await uploadFile(file);
      }
    }
  }, [brandId, expertId, agentId, maxFiles]);

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.fileId !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
    setProcessingStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileId];
      return newStatus;
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
      case 'completed': 
        return <CheckCircleIcon color="success" />;
      case 'failed': 
        return <ErrorIcon color="error" />;
      case 'processing':
      case 'ingested':
        return <CircularProgress size={20} />;
      default: 
        return <InsertDriveFileIcon />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Upload Area */}
      <Paper
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          bgcolor: isDragActive ? 'primary.50' : 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.50'
          },
          transition: 'all 0.2s ease'
        }}
        onClick={() => document.getElementById('file-upload-input').click()}
      >
        <input
          id="file-upload-input"
          type="file"
          multiple
          accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.json"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
        
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? "Drop files here" : "Upload Business Documents"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag & drop files here or click to browse
        </Typography>
        <Typography variant="caption" display="block">
          Supports: PDF, DOCX, CSV, Excel, TXT, MD, JSON (max 50MB each, {maxFiles} files max)
        </Typography>
      </Paper>
      
      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Uploading...</Typography>
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <Box key={fileId} sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                {fileId.split('_')[0]}: {progress}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          ))}
        </Box>
      )}
      
      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Uploaded Documents ({uploadedFiles.length})
          </Typography>
          <List>
          {uploadedFiles.map(file => (
            <ListItem key={file.fileId}>
              <ListItemIcon>
                {getStatusIcon(processingStatus[file.fileId])}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">
                      {file.filename || file.originalFile?.name}
                    </Typography>
                    {file.method && (
                      <Chip 
                        label={file.method === 'gemini_file_search' ? 'AI Search' : 'Legacy'}
                        icon={file.method === 'gemini_file_search' ? <CloudIcon /> : <FolderIcon />}
                        size="small"
                        color={file.method === 'gemini_file_search' ? 'primary' : 'default'}
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" display="block">
                      Status: {processingStatus[file.fileId] || 'pending'} • Size: {formatFileSize(file.file_size || file.originalFile?.size)}
                    </Typography>
                    {file.method === 'gemini_file_search' && (
                      <Typography variant="caption" color="primary" display="block">
                        Using AI-powered File Search for optimal retrieval
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => removeFile(file.fileId)} size="small">
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          </List>
        </Box>
      )}
      
      {/* Errors */}
      {errors.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {errors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              <strong>{error.filename}:</strong> {error.error}
            </Alert>
          ))}
          <Button size="small" onClick={() => setErrors([])} sx={{ mt: 1 }}>
            Clear Errors
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DocumentUploader;
